import { useState } from 'react'
import { useNavigate } from 'react-router'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/shadcn/card'
import { Button } from '@/components/ui/shadcn/button'
import { Input } from '@/components/ui/shadcn/input'
import { Badge } from '@/components/ui/shadcn/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/shadcn/select'
import { trpc } from '@/config/trpc'
import { ArrowLeft, RefreshCw } from 'lucide-react'

const TIME_RANGES = [
  { value: '15m', label: '15 min' },
  { value: '1h', label: '1 hour' },
  { value: '6h', label: '6 hours' },
  { value: '24h', label: '24 hours' },
  { value: '7d', label: '7 days' },
] as const

export function AdminLogsPage() {
  const navigate = useNavigate()

  const [level, setLevel] = useState<'all' | 'error' | 'warn'>('all')
  const [timeRange, setTimeRange] = useState('1h')
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [autoRefresh, setAutoRefresh] = useState(false)
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)

  const { data, isLoading, refetch } = trpc.logs.query.useQuery(
    {
      level,
      timeRange: timeRange as '15m' | '1h' | '6h' | '24h' | '7d',
      search: search || undefined,
      limit: 100,
    },
    {
      refetchInterval: autoRefresh ? 10_000 : false,
    },
  )

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearch(searchInput)
  }

  return (
    <div className="min-h-screen p-8">
      <div className="mx-auto max-w-7xl">
        <header className="mb-6">
          <Button variant="ghost" className="mb-2" onClick={() => navigate('/admin')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Admin
          </Button>
          <h1 className="text-3xl font-bold">Logs</h1>
          <p className="text-muted-foreground mt-1">{data?.logGroupName ?? 'CloudWatch Logs'}</p>
        </header>

        {/* Filter bar */}
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <Select value={level} onValueChange={(v) => setLevel(v as typeof level)}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All levels</SelectItem>
              <SelectItem value="error">Error</SelectItem>
              <SelectItem value="warn">Warn</SelectItem>
            </SelectContent>
          </Select>

          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIME_RANGES.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <form onSubmit={handleSearch} className="flex items-center gap-2">
            <Input
              placeholder="Search logs..."
              className="w-64"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <Button type="submit" variant="outline" size="sm">
              Search
            </Button>
          </form>

          <div className="ml-auto flex items-center gap-2">
            <Button
              variant={autoRefresh ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}>
              <RefreshCw className={`mr-1 h-3.5 w-3.5 ${autoRefresh ? 'animate-spin' : ''}`} />
              {autoRefresh ? 'Auto' : 'Auto'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Refresh
            </Button>
          </div>
        </div>

        {/* Log entries */}
        <Card>
          <CardHeader className="border-b px-6 py-3">
            <CardTitle className="flex items-center justify-between text-sm">
              <span>Log entries</span>
              <span className="text-muted-foreground font-normal">
                {data?.entries.length ?? 0} entries
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="max-h-[70vh] overflow-y-auto p-0">
            {isLoading && (
              <div className="text-muted-foreground p-8 text-center">Loading logs...</div>
            )}

            {!isLoading && data?.entries.length === 0 && (
              <div className="text-muted-foreground p-8 text-center">No log entries found</div>
            )}

            {data?.entries.map((entry, i) => (
              <div
                key={i}
                className="hover:bg-hover cursor-pointer border-b px-4 py-2.5"
                onClick={() => setExpandedIndex(expandedIndex === i ? null : i)}>
                <div className="flex items-center gap-2 text-sm">
                  <LevelBadge level={entry.level} />

                  <span className="text-muted-foreground shrink-0 font-mono text-xs">
                    {formatTimestamp(entry.timestamp)}
                  </span>

                  {entry.route && (
                    <Badge variant="outline" className="shrink-0 text-xs font-normal">
                      {entry.route}
                    </Badge>
                  )}

                  {entry.platform && (
                    <Badge variant="secondary" className="shrink-0 text-xs font-normal">
                      {entry.platform}
                    </Badge>
                  )}

                  <span className="text-foreground min-w-0 truncate">{entry.message}</span>

                  {entry.userId && (
                    <span className="text-muted-foreground shrink-0 font-mono text-xs">
                      user:{entry.userId}
                    </span>
                  )}
                </div>

                {expandedIndex === i && entry.stack && (
                  <pre className="bg-muted mt-2 overflow-x-auto whitespace-pre-wrap rounded p-3 font-mono text-xs">
                    {entry.stack}
                  </pre>
                )}

                {expandedIndex === i && !entry.stack && (
                  <pre className="bg-muted mt-2 overflow-x-auto whitespace-pre-wrap rounded p-3 font-mono text-xs">
                    {entry.raw}
                  </pre>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function LevelBadge({ level }: { level: string }) {
  if (level === 'error') {
    return (
      <Badge variant="destructive" className="shrink-0 text-xs">
        ERR
      </Badge>
    )
  }
  if (level === 'warn') {
    return (
      <Badge className="shrink-0 bg-yellow-600 text-xs text-white hover:bg-yellow-700">WRN</Badge>
    )
  }
  return (
    <Badge variant="secondary" className="shrink-0 text-xs">
      INF
    </Badge>
  )
}

function formatTimestamp(iso: string) {
  const d = new Date(iso)
  return d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}
